import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'
import {
	currentTargetRequired,
	getAttributeOrThrow,
	qsaOptional,
	qsRequired,
} from '@/scripts/core/global'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNames from 'embla-carousel-class-names'
import { type UgcPopup } from '@/scripts/theme/ugc-popup'
import { UcoastVideo } from '@/scripts/core/ucoast-video'

export class UgcSlider extends UcoastEl {
	static htmlSelector = 'ugc-slider'
	uniqueId: string
	ugcPopup: UgcPopup
	listenersAdded: boolean
	startIndex: number
	emblaApi?: EmblaCarouselType
	hasPreloadedAll: boolean
	constructor() {
		super()
		this.hasPreloadedAll = false
		this.listenersAdded = false
		this.uniqueId = getAttributeOrThrow('data-unique-id', this)
		this.startIndex = parseInt(getAttributeOrThrow('data-start-index', this))
		this.loadSlidesInView = this.loadSlidesInView.bind(this)
		this.addListeners = this.addListeners.bind(this)
		this.ugcPopup = qsRequired('ugc-popup')
	}

	onCtaClick(event: Event) {
		event.preventDefault()
		const target = currentTargetRequired(event)
		const ctaUrl = getAttributeOrThrow('data-cta-opener', target)
		if (ctaUrl === 'no_url') {
			throw Error('no url for metaobject ugc shop the look - check store configuration')
		}
		void this.ugcPopup.show(target, ctaUrl).then(() => {
			this.pauseAllSlides()
			this.preloadAllSlides()
		})
	}

	override animateIn() {
		super.animateIn()
		void this.initializeSlider()
	}

	initializeSlider() {
		const OPTIONS: EmblaOptionsType = {
			slidesToScroll: 1,
			align: 'center',
			containScroll: 'keepSnaps',
			dragFree: false,
			duration: 20,
			dragThreshold: 20,
			loop: true,
			startIndex: this.startIndex
		}

		const viewportNode = qsRequired('.embla__viewport', this)
		const emblaApi = EmblaCarousel(viewportNode, OPTIONS, [
			WheelGesturesPlugin(),
			ClassNames(),
		])
		emblaApi.on('init', () => {
			window.setTimeout(() => {
				this.loadSlidesInView()
				this.pauseSlidesOutsideView()
				this.addListeners()
			}, 100)
		})
		emblaApi.on('slidesInView', () => {
			if (!this.popupIsOpen()) {
				this.loadSlidesInView()
				this.pauseSlidesOutsideView()
				this.addListeners()
			}
		})
		this.emblaApi = emblaApi
	}
	loadSlidesInView() {
		qsaOptional('.is-in-view', this)?.forEach((slide) => {
			void window.Ucoast.mediaManager.loadAllInContainer(slide)
		})
	}
	pauseSlidesOutsideView() {
		qsaOptional('[data-embla-slide]:not(.is-in-view)', this)?.forEach((slide) => {
			void window.Ucoast.mediaManager.pauseAllInContainer(slide)
		})
	}
	preloadAllSlides() {
		if (this.hasPreloadedAll) return
		window.Ucoast.mediaManager.preloadContainer(this).then(() => {
			this.hasPreloadedAll = true
		})
	}
	pauseAllSlides() {
		const videos = qsaOptional<UcoastVideo>('[data-embla-slide] ucoast-video', this)
		if (!videos) return
		videos.forEach((slide) => {
			void slide.pause()
		})
	}
	playSlidesInView() {
		const videos = qsaOptional<UcoastVideo>('.is-in-view ucoast-video', this)
		if (!videos) return
		videos.forEach((slide) => {
			if (slide.isPlaying) return
			void slide.play()
		})
	}
	addListeners() {
		if (this.listenersAdded) return
		const openers = qsaOptional('[data-cta-opener]', this);
		if (!openers || openers?.length === 0) return
		openers?.forEach(button => {
			button.addEventListener('click', this.onCtaClick.bind(this))
		})
		this.listenersAdded = true
	}
	getCurrentSlideDetails() {
		const cta = qsRequired('.is-snapped[data-embla-slide] [data-cta-opener]')
		const url = getAttributeOrThrow('data-cta-opener', cta)
		return {
			cta,
			url
		}
	}
	popupIsOpen() {
		return this.ugcPopup.hasAttribute('open')
	}
}
